import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { PENDING, IDLE, REVIEW, WORKFLOW_RERUN, WORKFLOW_LEVEL_ASSIGNED } from "const"
import {
    CcReviewReviewItemCreate,
    CcReviewReviewItem,
    CcReviewReviewItemState,
    CcReviewReviewHistoryCreate,
    CcReviewReviewHistory,
} from "server/models"
import {
    useInput,
    StateType,
    CcStateSettingsType,
} from "client/utils"
import {
    StoreType,
    ReviewReviewItemValidation,

    SaveCcReviewReviewItem,
    CreateCcReviewReviewRevision,
    CreateCcReviewReviewState,
    UpdateCcReviewReviewState,
    CreateCcReviewReviewHistory,
    CreateManyCcReviewReviewHistory,
    LoadCcReviewReviewItemState,
    LoadCcReviewReviewHistory,
    SetCcReviewReviewEditHref,

    setCcReviewReviewItemDetails,
    setCcReviewReviewItemState,
    setCcReviewReviewItemHistory,
    setCcReviewReviewReloadList,
    setCcReviewReviewReloadAnswer,
    setCcReviewReviewReloadEdit,
    closeModal,

    initReviewReviewHistory,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Textarea,
    Feedback,
    SelectDropdown,
    SelectDropdownItem,
    UserIcon,
    Button,
    SpinnerIcon,
} from "client/components"

interface EditReviewItemProps {
    item: CcReviewReviewItem
    revisionId: number
}

let isMounted: boolean

const Form = ({
    item,
    revisionId,
}: EditReviewItemProps) => {
    const baseRoute = useSelector((state: StoreType) => state.ccReviewDocument.baseRoute)
    const activeFile = useSelector((state: StoreType) => state.ccReviewDocument.activeFile)
    const activeVersion = useSelector((state: StoreType) => state.ccReviewDocument.activeVersion)

    const itemState = useSelector((state: StoreType) => state.ccReviewReview.itemState)
    const itemHistory = useSelector((state: StoreType) => state.ccReviewReview.itemHistory)

    const levelList = useSelector((state: StoreType) => state.ccApprovalLevel.levelList)
    const moduleLevelList = useSelector((state: StoreType) => state.ccApprovalLevel.moduleLevelList)
    const levelIds = moduleLevelList[2] || [] // review level ids

    const typeList = useSelector((state: StoreType) => state.ccReviewReviewType.typeList)
    const typeIds = useSelector((state: StoreType) => state.ccReviewReviewType.typeIds)

    const groupUserList = useSelector((state: StoreType) => state.projectUserGroup.groupUserList)
    const userList = useSelector((state: StoreType) => state.user.userList)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()


    const [remark, setRemark] = useInput(item.remark)
    const [page, setPage] = useInput(item.page)

    const [creator] = useState(item.id ? item.creator : userData.userId)
    const [version, setVersion] = useState(item.id ? item.version : activeVersion)
    const [selectedUserId, setSelectedUserId] = useState<number>(null)
    const [typeId, setTypeId] = useState(item.id ? item.typeId : typeIds[0])

    const [validation, setValidation] = useState<ReviewReviewItemValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)


    const selectedLevelId = levelIds.length ? levelIds[0] : null // first level

    const selectedLevel = selectedLevelId
        ? levelList[selectedLevelId]
        : null

    const groupId = selectedLevel
        ? selectedLevel.groupId
        : null

    const userIds = groupId
        ? groupUserList[groupId] || []
        : []

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    useEffect(() => {
        if (item.id) {
            const settings: CcStateSettingsType = itemState && itemState.settings ? JSON.parse(itemState.settings) : {}
            const prevUserId = settings[selectedLevelId] || null
            if (prevUserId && userIds && userIds.includes(prevUserId)) setSelectedUserId(prevUserId)
            else setSelectedUserId(null)
        }
        if (!item.id) {
            if (userIds && userIds.includes(activeVersion.owner)) setSelectedUserId(activeVersion.owner)
            else setSelectedUserId(null)
        }
    }, [item, itemState, selectedLevelId, userIds, activeVersion])

    const onClose = () => {
        if (!isMounted) return
        if (modals.length && isMounted) {
            dispatch(closeModal()) // can be opened only in modal
        }
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const newItemData: CcReviewReviewItemCreate = {
            projectId: item.projectId || project.id,
            fileId: item.fileId || activeFile.id,
            versionId: version ? version.id : null,
            remark,
            page,
            typeId,
            creator,
        }

        SaveCcReviewReviewItem(item.id, newItemData, lang).then((resItem) => {
            if (resItem.validation) {
                if (isMounted) {
                    setValidation(resItem.validation)
                    setSaveState(IDLE)
                    setApplyState(IDLE)
                }
                return
            }

            const promiseArray: Promise<any>[] = []

            // create revision and state/history for new review item
            if (resItem.newItemId) {
                promiseArray.push(
                    CreateCcReviewReviewRevision(resItem.newItemId)
                    .then((resRevision) => {
                        const nestedPromiseArray: Promise<any>[] = []

                        if (selectedLevelId && selectedUserId) {
                            const newSettings: CcStateSettingsType = { [selectedLevelId]: selectedUserId }
                            const newItemState: CcReviewReviewItemState = {
                                itemId: resItem.newItemId,
                                reviewReviewRevisionId: resRevision.id,
                                levelId: selectedLevelId,
                                verifierId: selectedUserId,
                                settings: JSON.stringify(newSettings),
                            }
                            const newHistory: CcReviewReviewHistoryCreate = {
                                ...initReviewReviewHistory,
                                itemId: resItem.newItemId,
                                reviewReviewRevisionId: resRevision.id,
                                parentId: null,
                                prevUserId: null,
                                prevLevelId: selectedLevelId,
                                nextUserId: selectedUserId,
                                workflow: WORKFLOW_LEVEL_ASSIGNED,
                                systemMessage: true,
                                editorId: userData.userId,
                            }

                            nestedPromiseArray.push(
                                CreateCcReviewReviewState(newItemState),
                                CreateCcReviewReviewHistory(newHistory, lang),
                            )
                        }

                        return Promise.all(nestedPromiseArray).then(() => {
                            if (isMounted) {
                                SetCcReviewReviewEditHref(
                                    baseRoute,
                                    REVIEW,
                                    activeFile.id,
                                    activeVersion.id,
                                    resItem.newItemId,
                                    resRevision.id,
                                )
                            }

                            return Promise.all([
                                LoadCcReviewReviewItemState(resItem.newItemId, resRevision.id).then((state) => {
                                    dispatch(setCcReviewReviewItemState(state))
                                }),
                                LoadCcReviewReviewHistory(resItem.newItemId, resRevision.id).then((history) => {
                                    dispatch(setCcReviewReviewItemHistory(history))
                                }),
                            ])
                        })
                    }),
                )
            }

            // update state and history
            if (item.id && selectedLevelId && selectedUserId) {
                const settings: CcStateSettingsType = itemState && itemState.settings ? JSON.parse(itemState.settings) : {}
                const prevUserId = settings[selectedLevelId]

                if (prevUserId !== selectedUserId) {
                    let lastHistory: CcReviewReviewHistory = null

                    // get the last history row made by user, not by 'system'
                    for (let i = itemHistory.length - 1; i >= 0; i--) {
                        const history = itemHistory[i]
                        if (history.workflow === WORKFLOW_RERUN) {
                            break
                        }
                        if (history.systemMessage === false) {
                            lastHistory = history
                            break
                        }
                    }

                    settings[selectedLevelId] = selectedUserId

                    const nextLevelId = (itemState && itemState.levelId) || selectedLevelId // where "selectedLevelId" is first level
                    const nextVerifierId = (itemState && itemState.verifierId) || null

                    const newItemState: CcReviewReviewItemState = {
                        itemId: item.id,
                        reviewReviewRevisionId: revisionId,
                        levelId: nextLevelId,
                        verifierId: nextLevelId === selectedLevelId ? selectedUserId : nextVerifierId,
                        settings: JSON.stringify(settings),
                    }
                    const newHistory: CcReviewReviewHistoryCreate = {
                        ...initReviewReviewHistory,
                        itemId: item.id,
                        reviewReviewRevisionId: revisionId,
                        parentId: lastHistory && lastHistory.nextLevelId ? lastHistory.id : null,
                        prevUserId: prevUserId || null,
                        prevLevelId: selectedLevelId,
                        nextUserId: selectedUserId,
                        workflow: WORKFLOW_LEVEL_ASSIGNED,
                        systemMessage: true,
                        editorId: userData.userId,
                    }

                    promiseArray.push(
                        Promise.all([
                            UpdateCcReviewReviewState(newItemState),
                            CreateManyCcReviewReviewHistory([newHistory]),
                        ]).then(() => Promise.all([
                            LoadCcReviewReviewItemState(item.id, revisionId).then((state) => {
                                dispatch(setCcReviewReviewItemState(state))
                            }),
                            LoadCcReviewReviewHistory(item.id, revisionId).then((history) => {
                                dispatch(setCcReviewReviewItemHistory(history))
                            }),
                        ])),
                    )
                }
            }

            Promise.all(promiseArray).then(() => {
                if (isMounted) {
                    setValidation(resItem.validation)
                    setSaveState(IDLE)
                    setApplyState(IDLE)
                }

                dispatch(setCcReviewReviewItemDetails({
                    ...newItemData,
                    id: resItem.newItemId || item.id,
                    version,
                }))

                dispatch(setCcReviewReviewReloadList(false))
                dispatch(setCcReviewReviewReloadAnswer(false))
                setTimeout(() => {
                    dispatch(setCcReviewReviewReloadList(true))
                    dispatch(setCcReviewReviewReloadAnswer(true))
                }, 0)

                if (mode === 1) onClose()
                if (mode === 2) {
                    dispatch(setCcReviewReviewReloadEdit(false))
                    setTimeout(() => {
                        dispatch(setCcReviewReviewReloadEdit(true))
                    }, 0)
                }
            })
        })
    }


    const user = userList[creator]
    const creatorName = user && `${user.firstName} ${user.lastName}`

    let disableSave = false
    if (
        remark.trim().length === 0
        || page.trim().length === 0
        || !version
        || !typeList[typeId]
    ) {
        disableSave = true
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <p>
                        <b>{creatorName}</b>
                        {` ${lang.reviewItemDescription}: `}
                        <b>{activeFile.name}</b>
                    </p>
                    <FormGroup>
                        <Textarea value={remark} onChange={setRemark} />
                        <Feedback>{validation && validation.remark}</Feedback>
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col xs={6}>
                    <FormGroup>
                        <Label>{lang.seePage}</Label>
                        <Input type="text" value={page} onChange={setPage} />
                        <Feedback>{validation && validation.page}</Feedback>
                    </FormGroup>
                </Col>
                <Col xs={6}>
                    <FormGroup>
                        <Label>{lang.kind}</Label>
                        <SelectDropdown title={typeList[typeId] ? typeList[typeId].name : ""}>
                            {
                                typeIds.map((id) => (
                                    <SelectDropdownItem key={id} onClick={() => setTypeId(id)}>
                                        {typeList[id].name}
                                    </SelectDropdownItem>
                                ))
                            }
                        </SelectDropdown>
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col xs={selectedLevel ? 6 : 12}>
                    <FormGroup>
                        <Label>{lang.revision}</Label>
                        <SelectDropdown title={version ? version.revision : ""}>
                            {
                                activeFile.versions.map((row) => (
                                    <SelectDropdownItem
                                        key={row.id}
                                        onClick={() => {
                                            setVersion(row)
                                            if (userIds && userIds.includes(row.owner)) setSelectedUserId(row.owner)
                                        }}
                                    >
                                        {row.revision}
                                    </SelectDropdownItem>
                                ))
                            }
                        </SelectDropdown>
                    </FormGroup>
                </Col>
                {
                    selectedLevel && (
                        <Col xs={6}>
                            <FormGroup>
                                <Label>{selectedLevel.name}</Label>
                                <SelectDropdown
                                    title={selectedUserId ? <UserIcon user={userList[selectedUserId]} size="sm" /> : lang.selectUser}
                                >
                                    {
                                        userIds && userIds.map((userId) => (
                                            <SelectDropdownItem
                                                key={userId}
                                                size="sm"
                                                onClick={() => setSelectedUserId(userId)}
                                            >
                                                <UserIcon user={userList[userId]} size="sm" />
                                            </SelectDropdownItem>
                                        ))
                                    }
                                </SelectDropdown>
                            </FormGroup>
                        </Col>
                    )
                }
            </Row>

            <Row>
                <Col xs={12}>
                    <Button
                        variant="success"
                        row
                        disabled={disableSave}
                        onClick={() => onSave(1)}
                    >
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button
                        variant="success"
                        row
                        disabled={disableSave}
                        onClick={() => onSave(2)}
                    >
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
